import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  FiShield,
  FiCode,
  FiEye,
  FiZap,
  FiArrowRight,
  FiCheckCircle,
  FiCpu,
  FiBarChart2,
  FiGlobe,
  FiVideo,
  FiAlertTriangle,
  FiMail,
  FiClock,
  FiLock,
  FiInfo,
  FiTarget,
  FiActivity,
  FiSend,
  FiFileText,
  FiUser,
  FiTerminal,
  FiExternalLink,
  FiLayers,
} from "react-icons/fi";

// The live demo report. A real generated report on a demo session, not a
// mockup — the questions tab is deliberately gated behind an upsell in
// UnifiedReport, so linking it gives away the analysis and not the question set.
const DEMO_REPORT_URL = "/public-report/demo-ai-engineer-1765955608885";

const Index = () => {
  const TALENT_OS_URL = import.meta.env.VITE_TALENT_OS_URL || "https://app.weremotedevs.com";
  const [showScoringDialog, setShowScoringDialog] = useState(false);

  // Capability facts, not volume claims. Every number here is checked against
  // the engine: 10 languages is LANGUAGE_NAMES in execute-code-sync, 7 question
  // types is QUESTION_TYPE_DEFAULTS in _shared/questionWeights.ts.
  const stats = [
    { value: "7", label: "Question types", icon: <FiLayers /> },
    { value: "10", label: "Languages executed", icon: <FiCode /> },
    { value: "< 2 min", label: "Report turnaround", icon: <FiClock /> },
    { value: "API + MCP", label: "Fully programmable", icon: <FiTerminal /> },
  ];

  const steps = [
    {
      icon: <FiTarget className="w-5 h-5" />,
      title: "Describe the role",
      body: "Job title, skills, experience level, and exactly how many questions of each type you want. The paper is built to that mix, not approximately to it.",
    },
    {
      icon: <FiEye className="w-5 h-5" />,
      title: "Review before it goes out",
      body: "Preview the generated set for free. Nothing is sent and nobody is charged until you are happy with the questions.",
    },
    {
      icon: <FiSend className="w-5 h-5" />,
      title: "Invite the candidate",
      body: "They get a link, take the assessment in the browser under AI proctoring, and write code that really runs against test cases.",
    },
    {
      icon: <FiFileText className="w-5 h-5" />,
      title: "Read the report",
      body: "Scored per question, per skill and per type, with an integrity score reported separately. Share it with a link.",
    },
  ];

  const features = [
    {
      icon: <FiCpu className="w-6 h-6" />,
      title: "Role-specific question generation",
      description:
        "Questions are written for the role in front of you from the skills, seniority and job description — not pulled from a fixed bank everyone has seen.",
    },
    {
      icon: <FiCode className="w-6 h-6" />,
      title: "Code that actually executes",
      description:
        "Submissions run in a sandbox across 10 languages and are graded on test cases that pass or fail, with partial credit for a partly-working solution.",
    },
    {
      icon: <FiVideo className="w-6 h-6" />,
      title: "AI proctoring",
      description:
        "Face, voice, phone and tab-switch monitoring throughout, recorded as timestamped events you can inspect afterwards rather than a single opaque verdict.",
    },
    {
      icon: <FiBarChart2 className="w-6 h-6" />,
      title: "Reports you can defend",
      description:
        "Every question shows what was asked, what they answered, and what it was worth. The totals reconcile, so a candidate disputing a score can be answered.",
    },
    {
      icon: <FiClock className="w-6 h-6" />,
      title: "Time fitted to the paper",
      description:
        "The clock is derived from the questions actually generated plus a 15% buffer, so a long paper is never handed an unfinishable duration.",
    },
    {
      icon: <FiLock className="w-6 h-6" />,
      title: "Private by default",
      description:
        "Row-level security, per-account isolation, and share links you switch on deliberately per report rather than reports being public until hidden.",
    },
  ];

  const questionTypes = [
    { name: "Multiple choice", note: "Knowledge and judgement" },
    { name: "Coding MCQ", note: "Read code, predict output" },
    { name: "Fill in the blank", note: "Precise recall" },
    { name: "Live coding", note: "Executed against test cases" },
    { name: "System design", note: "AI-graded against a rubric" },
    { name: "Essay", note: "AI-graded against a rubric" },
    { name: "Scenario", note: "Applied problem solving" },
  ];

  const languages = [
    "Python", "JavaScript", "TypeScript", "Java", "C++",
    "C", "Go", "Ruby", "PHP", "Rust",
  ];

  const proctoring = [
    { icon: <FiEye />, label: "Face detection", desc: "Present, absent, or more than one" },
    { icon: <FiVideo />, label: "Voice monitoring", desc: "Second voice and AI-voice signals" },
    { icon: <FiAlertTriangle />, label: "Phone detection", desc: "Object recognition on camera" },
    { icon: <FiGlobe />, label: "Tab and focus", desc: "Switches, blur, fullscreen exits" },
  ];

  const faqs = [
    {
      q: "Where do the questions come from?",
      a: "They are generated for each role from the skills, seniority and job description you give, so two companies hiring the same title do not send the same paper. You can also upload your own questions or draw from a saved bank.",
    },
    {
      q: "Can a candidate just paste the question into ChatGPT?",
      a: "Tab switches, window blur, paste events and fullscreen exits are all recorded with timestamps and shown on the report, and coding answers are graded on execution rather than on how the code reads. Proctoring lowers the integrity score; it never silently changes the skill score, so you always see both numbers and decide yourself.",
    },
    {
      q: "How is the score calculated?",
      a: "Points earned divided by points available, as a percentage. Each question carries a weight set by its type and difficulty — a hard coding question is worth several MCQs. Nothing is subtracted for proctoring flags. The full breakdown is on this page under Scoring methodology.",
    },
    {
      q: "Is there negative marking?",
      a: "No. A wrong answer scores zero, never below it. An unanswered question also scores zero and still counts against the total, so skipping is not a way to protect a percentage.",
    },
    {
      q: "How long does a report take?",
      a: "Usually under two minutes after submission. The report is generated server-side and the invited client is notified when it is ready.",
    },
    {
      q: "Can I use this from my own system?",
      a: "Yes. Everything the dashboard does is available over the REST API at api.weremotedevs.com, and the same operations are exposed as MCP tools at mcp.weremotedevs.com so an AI assistant can run the flow directly.",
    },
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      {/* ── Nav ─────────────────────────────────────────────────────────── */}
      <header className="sticky top-0 z-50 border-b border-white/10 bg-slate-950/80 backdrop-blur-xl">
        <div className="container mx-auto flex items-center justify-between gap-4 px-4 py-3.5">
          <a href="/" className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-violet-500 to-indigo-600">
              <FiShield className="h-4.5 w-4.5 text-white" />
            </div>
            <div className="leading-tight">
              <div className="text-[15px] font-semibold text-white">SkillProof</div>
              <div className="text-[11px] text-slate-500">by WeRemoteDevs</div>
            </div>
          </a>

          <nav className="hidden items-center gap-7 text-sm text-slate-400 lg:flex">
            <a href="#how" className="transition-colors hover:text-white">How it works</a>
            <a href="#features" className="transition-colors hover:text-white">Features</a>
            <a href="#report" className="transition-colors hover:text-white">Sample report</a>
            <a href="#integrity" className="transition-colors hover:text-white">Integrity</a>
            <a href="#api" className="transition-colors hover:text-white">API</a>
          </nav>

          <div className="flex items-center gap-2">
            <a
              href="#candidates"
              className="hidden rounded-lg px-3 py-2 text-sm text-slate-300 transition-colors hover:text-white sm:inline-block"
            >
              Candidate login
            </a>
            <Button
              size="sm"
              className="bg-white text-slate-900 hover:bg-slate-200"
              onClick={() => window.open(TALENT_OS_URL, "_blank")}
            >
              Get started
            </Button>
          </div>
        </div>
      </header>

      {/* ── Hero ────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden border-b border-white/5">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -top-40 left-1/2 h-96 w-[42rem] -translate-x-1/2 rounded-full bg-violet-600/20 blur-[128px]" />
        </div>

        <div className="container relative mx-auto px-4 py-20 sm:py-28">
          <div className="mx-auto max-w-3xl text-center">
            <Badge className="mb-6 border-violet-400/30 bg-violet-500/10 px-3 py-1 text-violet-300 hover:bg-violet-500/10">
              Technical assessments, generated per role
            </Badge>

            <h1 className="text-4xl font-bold leading-[1.1] tracking-tight text-white sm:text-6xl">
              Know if they can do the job
              <span className="block bg-gradient-to-r from-violet-400 to-indigo-400 bg-clip-text text-transparent">
                before the first interview
              </span>
            </h1>

            <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-slate-400">
              SkillProof writes a technical assessment for the specific role you are
              hiring for, watches the candidate take it, runs their code against real
              test cases, and hands you a report you can actually defend in a hiring
              debrief.
            </p>

            <div className="mt-9 flex flex-col justify-center gap-3 sm:flex-row">
              <Button
                size="lg"
                className="bg-gradient-to-r from-violet-500 to-indigo-600 px-7 text-white hover:from-violet-600 hover:to-indigo-700"
                onClick={() => window.open(TALENT_OS_URL, "_blank")}
              >
                Get started <FiArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-white/20 bg-white/5 px-7 text-white hover:bg-white/10 hover:text-white"
                onClick={() => window.open(DEMO_REPORT_URL, "_blank")}
              >
                See a real report <FiExternalLink className="ml-2 h-4 w-4" />
              </Button>
            </div>

            <p className="mt-5 text-sm text-slate-500">
              Previewing questions is free. You are only charged when an assessment is sent.
            </p>
          </div>

          {/* Stat strip */}
          <div className="mx-auto mt-16 grid max-w-4xl grid-cols-2 gap-px overflow-hidden rounded-2xl border border-white/10 bg-white/10 lg:grid-cols-4">
            {stats.map((s) => (
              <div key={s.label} className="bg-slate-950 px-4 py-6 text-center">
                <div className="mb-2 flex justify-center text-violet-400">{s.icon}</div>
                <div className="text-2xl font-semibold text-white">{s.value}</div>
                <div className="mt-1 text-xs text-slate-500">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── What it is ──────────────────────────────────────────────────── */}
      <section className="border-b border-white/5 py-20">
        <div className="container mx-auto max-w-5xl px-4">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold text-white sm:text-4xl">
              A CV tells you what they claim. This tells you what they can do.
            </h2>
            <p className="mt-4 text-slate-400">
              Screening calls are slow and easy to talk your way through. Take-homes get
              outsourced. Generic question banks leak. SkillProof closes all three gaps.
            </p>
          </div>

          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {[
              {
                icon: <FiTarget className="h-5 w-5" />,
                title: "Written for your role",
                body: "The paper comes from your skills list, seniority and job description, so it tests the work rather than general trivia — and it is not a set that has been circulating online for years.",
              },
              {
                icon: <FiCode className="h-5 w-5" />,
                title: "Proved by execution",
                body: "Coding answers are compiled and run against test cases. Passing is passing. There is no marker deciding whether code that never ran looks about right.",
              },
              {
                icon: <FiBarChart2 className="h-5 w-5" />,
                title: "Reported transparently",
                body: "Every question, answer and weight is shown. If a candidate challenges a score you can open the report and point at the arithmetic.",
              },
            ].map((c) => (
              <Card key={c.title} className="border-white/10 bg-white/[0.03]">
                <CardContent className="p-6">
                  <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-lg bg-violet-500/15 text-violet-400">
                    {c.icon}
                  </div>
                  <h3 className="mb-2 font-semibold text-white">{c.title}</h3>
                  <p className="text-sm leading-relaxed text-slate-400">{c.body}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ── How it works ────────────────────────────────────────────────── */}
      <section id="how" className="scroll-mt-20 border-b border-white/5 py-20">
        <div className="container mx-auto max-w-5xl px-4">
          <div className="mx-auto max-w-2xl text-center">
            <Badge variant="outline" className="mb-4 border-white/15 text-slate-400">
              How it works
            </Badge>
            <h2 className="text-3xl font-bold text-white sm:text-4xl">Four steps, start to decision</h2>
          </div>

          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {steps.map((s, i) => (
              <div key={s.title} className="relative rounded-xl border border-white/10 bg-white/[0.03] p-6">
                <div className="mb-4 flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-violet-500 to-indigo-600 text-white">
                    {s.icon}
                  </div>
                  <span className="text-xs font-medium tracking-wider text-slate-600">
                    STEP {i + 1}
                  </span>
                </div>
                <h3 className="mb-2 font-semibold text-white">{s.title}</h3>
                <p className="text-sm leading-relaxed text-slate-400">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Sample report ───────────────────────────────────────────────── */}
      <section id="report" className="scroll-mt-20 border-b border-white/5 py-20">
        <div className="container mx-auto max-w-5xl px-4">
          <div className="overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-violet-500/10 to-indigo-500/5">
            <div className="grid items-center gap-10 p-8 sm:p-12 lg:grid-cols-2">
              <div>
                <Badge variant="outline" className="mb-4 border-white/15 text-slate-400">
                  Sample report
                </Badge>
                <h2 className="text-3xl font-bold leading-tight text-white">
                  Look at what you actually receive
                </h2>
                <p className="mt-4 leading-relaxed text-slate-400">
                  This is a real generated report for a demo AI Engineer assessment —
                  not a screenshot and not a mockup. Open it and read the same page a
                  hiring manager reads.
                </p>

                <ul className="mt-6 space-y-3">
                  {[
                    "Overall score against the pass mark you set",
                    "Per-skill and per-type breakdown",
                    "Strengths, gaps and a hiring recommendation",
                    "Integrity score reported separately from ability",
                    "Every question, answer and weight, itemised",
                  ].map((t) => (
                    <li key={t} className="flex items-start gap-3 text-sm text-slate-300">
                      <FiCheckCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-violet-400" />
                      {t}
                    </li>
                  ))}
                </ul>

                <Button
                  size="lg"
                  className="mt-8 bg-white text-slate-900 hover:bg-slate-200"
                  onClick={() => window.open(DEMO_REPORT_URL, "_blank")}
                >
                  Open the sample report <FiArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>

              {/* Report preview card — mirrors the real demo report's figures */}
              <div className="rounded-xl border border-white/10 bg-slate-900/70 p-6 shadow-2xl">
                <div className="mb-5 flex items-start justify-between">
                  <div>
                    <div className="text-sm font-semibold text-white">AI Engineer Assessment</div>
                    <div className="text-xs text-slate-500">Demo Candidate · 21 questions</div>
                  </div>
                  <Badge className="border-emerald-400/30 bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/15">
                    Strong hire
                  </Badge>
                </div>

                <div className="mb-5 flex items-end gap-2">
                  <span className="text-5xl font-bold text-white">86</span>
                  <span className="mb-1.5 text-xl text-slate-500">%</span>
                  <span className="mb-2 ml-auto text-xs text-slate-500">Pass mark 60%</span>
                </div>

                <div className="space-y-3">
                  {[
                    { label: "Python", value: 92 },
                    { label: "Machine learning", value: 84 },
                    { label: "System design", value: 71 },
                  ].map((s) => (
                    <div key={s.label}>
                      <div className="mb-1.5 flex justify-between text-xs">
                        <span className="text-slate-400">{s.label}</span>
                        <span className="text-slate-300">{s.value}%</span>
                      </div>
                      <div className="h-1.5 overflow-hidden rounded-full bg-white/10">
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-violet-500 to-indigo-500"
                          style={{ width: `${s.value}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-5 flex items-center gap-2 border-t border-white/10 pt-4 text-xs text-slate-500">
                  <FiShield className="h-3.5 w-3.5 text-emerald-400" />
                  Integrity score reported separately — never deducted from ability
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Features ────────────────────────────────────────────────────── */}
      <section id="features" className="scroll-mt-20 border-b border-white/5 py-20">
        <div className="container mx-auto max-w-5xl px-4">
          <div className="mx-auto max-w-2xl text-center">
            <Badge variant="outline" className="mb-4 border-white/15 text-slate-400">
              Features
            </Badge>
            <h2 className="text-3xl font-bold text-white sm:text-4xl">
              Everything the assessment needs to be believed
            </h2>
          </div>

          <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {features.map((f) => (
              <Card key={f.title} className="border-white/10 bg-white/[0.03] transition-colors hover:bg-white/[0.06]">
                <CardContent className="p-6">
                  <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-lg bg-violet-500/15 text-violet-400">
                    {f.icon}
                  </div>
                  <h3 className="mb-2 font-semibold text-white">{f.title}</h3>
                  <p className="text-sm leading-relaxed text-slate-400">{f.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ── Question types & languages ──────────────────────────────────── */}
      <section className="border-b border-white/5 py-20">
        <div className="container mx-auto max-w-5xl px-4">
          <div className="grid gap-8 lg:grid-cols-2">
            <Card className="border-white/10 bg-white/[0.03]">
              <CardContent className="p-8">
                <h3 className="mb-1 text-xl font-semibold text-white">Seven question types</h3>
                <p className="mb-6 text-sm text-slate-500">
                  Ask for the exact mix you want. The paper is held to it.
                </p>
                <div className="space-y-2.5">
                  {questionTypes.map((q) => (
                    <div
                      key={q.name}
                      className="flex items-center justify-between rounded-lg border border-white/5 bg-white/[0.02] px-4 py-2.5"
                    >
                      <span className="text-sm font-medium text-slate-200">{q.name}</span>
                      <span className="text-xs text-slate-500">{q.note}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="border-white/10 bg-white/[0.03]">
              <CardContent className="p-8">
                <h3 className="mb-1 text-xl font-semibold text-white">Ten languages, really run</h3>
                <p className="mb-6 text-sm text-slate-500">
                  Code is compiled and executed in a sandbox against test cases, with
                  partial credit when some tests pass.
                </p>
                <div className="flex flex-wrap gap-2">
                  {languages.map((l) => (
                    <span
                      key={l}
                      className="rounded-lg border border-white/10 bg-white/[0.04] px-3 py-1.5 text-sm text-slate-300"
                    >
                      {l}
                    </span>
                  ))}
                </div>
                <div className="mt-6 rounded-lg border border-violet-400/20 bg-violet-500/10 p-4">
                  <div className="mb-1.5 flex items-center gap-2 text-sm font-medium text-violet-300">
                    <FiZap className="h-4 w-4" /> Bring your own questions
                  </div>
                  <p className="text-xs leading-relaxed text-slate-400">
                    Upload your own set or reuse a saved bank instead of generating,
                    and mix the two in a single paper.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* ── Integrity ───────────────────────────────────────────────────── */}
      <section id="integrity" className="scroll-mt-20 border-b border-white/5 py-20">
        <div className="container mx-auto max-w-5xl px-4">
          <div className="mx-auto max-w-2xl text-center">
            <Badge variant="outline" className="mb-4 border-white/15 text-slate-400">
              Integrity
            </Badge>
            <h2 className="text-3xl font-bold text-white sm:text-4xl">Proctoring that reports, not accuses</h2>
            <p className="mt-4 text-slate-400">
              Every signal is stored as a timestamped event you can read on the report.
              They lower the integrity score, which is shown next to the skill score and
              never subtracted from it — the judgement stays yours.
            </p>
          </div>

          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {proctoring.map((p) => (
              <Card key={p.label} className="border-white/10 bg-white/[0.03] text-center">
                <CardContent className="p-6">
                  <div className="mx-auto mb-4 flex h-11 w-11 items-center justify-center rounded-lg bg-violet-500/15 text-xl text-violet-400">
                    {p.icon}
                  </div>
                  <h3 className="mb-1 text-sm font-semibold text-white">{p.label}</h3>
                  <p className="text-xs text-slate-500">{p.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-8 text-center">
            <Button
              variant="outline"
              className="border-white/20 bg-white/5 text-white hover:bg-white/10 hover:text-white"
              onClick={() => setShowScoringDialog(true)}
            >
              <FiInfo className="mr-2 h-4 w-4" /> Read the scoring methodology
            </Button>
          </div>
        </div>
      </section>

      {/* ── API & MCP ───────────────────────────────────────────────────── */}
      <section id="api" className="scroll-mt-20 border-b border-white/5 py-20">
        <div className="container mx-auto max-w-5xl px-4">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div>
              <Badge variant="outline" className="mb-4 border-white/15 text-slate-400">
                For engineering teams
              </Badge>
              <h2 className="text-3xl font-bold leading-tight text-white sm:text-4xl">
                Run the whole flow from your own stack
              </h2>
              <p className="mt-4 leading-relaxed text-slate-400">
                Everything the dashboard does is available over REST, and the same
                operations are exposed as MCP tools so an AI assistant can preview
                questions, send assessments and pull reports directly.
              </p>

              <div className="mt-6 space-y-3">
                {[
                  ["Preview questions", "Free. Nothing sent, nothing charged."],
                  ["Send assessments", "One credit per candidate, charged per send."],
                  ["Read reports", "Poll, or receive a webhook when one lands."],
                ].map(([t, d]) => (
                  <div key={t} className="flex items-start gap-3">
                    <FiCheckCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-violet-400" />
                    <div>
                      <div className="text-sm font-medium text-slate-200">{t}</div>
                      <div className="text-xs text-slate-500">{d}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-7 flex flex-wrap gap-2">
                <Badge className="border-white/10 bg-white/5 text-slate-300 hover:bg-white/5">REST API</Badge>
                <Badge className="border-white/10 bg-white/5 text-slate-300 hover:bg-white/5">MCP server</Badge>
                <Badge className="border-white/10 bg-white/5 text-slate-300 hover:bg-white/5">Webhooks</Badge>
                <Badge className="border-white/10 bg-white/5 text-slate-300 hover:bg-white/5">OAuth 2.1</Badge>
              </div>
            </div>

            <div className="overflow-hidden rounded-xl border border-white/10 bg-slate-900/80">
              <div className="flex items-center gap-2 border-b border-white/10 px-4 py-3">
                <div className="flex gap-1.5">
                  <div className="h-2.5 w-2.5 rounded-full bg-red-500/60" />
                  <div className="h-2.5 w-2.5 rounded-full bg-yellow-500/60" />
                  <div className="h-2.5 w-2.5 rounded-full bg-green-500/60" />
                </div>
                <span className="ml-2 text-xs text-slate-500">Send an assessment</span>
              </div>
              <ScrollArea className="max-h-[22rem]">
                <pre className="overflow-x-auto p-5 text-[12.5px] leading-relaxed">
                  <code className="text-slate-300">
                    <span className="text-emerald-400">curl</span>{" -X POST "}
                    <span className="text-violet-300">https://api.weremotedevs.com/v1/vetting</span>{"\n"}
                    {"  -H "}<span className="text-amber-300">"Authorization: Bearer $WRD_API_KEY"</span>{" \\\n"}
                    {"  -H "}<span className="text-amber-300">"Content-Type: application/json"</span>{" \\\n"}
                    {"  -d "}<span className="text-amber-300">{"'{"}</span>{"\n"}
                    {"    "}<span className="text-sky-300">"candidate_email"</span>{": "}
                    <span className="text-amber-300">"dev@example.com"</span>{",\n"}
                    {"    "}<span className="text-sky-300">"job_title"</span>{": "}
                    <span className="text-amber-300">"Senior Python Engineer"</span>{",\n"}
                    {"    "}<span className="text-sky-300">"skills"</span>{": ["}
                    <span className="text-amber-300">"Python"</span>{", "}
                    <span className="text-amber-300">"PostgreSQL"</span>{"],\n"}
                    {"    "}<span className="text-sky-300">"question_counts"</span>{": { "}
                    <span className="text-sky-300">"mcq"</span>{": 6, "}
                    <span className="text-sky-300">"coding"</span>{": 2 },\n"}
                    {"    "}<span className="text-sky-300">"passing_score"</span>{": 70\n"}
                    {"  "}<span className="text-amber-300">{"}'"}</span>
                  </code>
                </pre>
              </ScrollArea>
              <div className="border-t border-white/10 px-5 py-3 text-xs text-slate-500">
                MCP endpoint · <span className="text-slate-400">mcp.weremotedevs.com/mcp</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Candidates ──────────────────────────────────────────────────── */}
      <section id="candidates" className="scroll-mt-20 border-b border-white/5 py-20">
        <div className="container mx-auto max-w-4xl px-4">
          <Card className="border-white/10 bg-white/[0.03]">
            <CardContent className="grid items-center gap-8 p-8 sm:p-10 md:grid-cols-[1fr_auto]">
              <div>
                <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-lg bg-violet-500/15 text-violet-400">
                  <FiUser className="h-5 w-5" />
                </div>
                <h2 className="text-2xl font-bold text-white">Taking an assessment?</h2>
                <p className="mt-3 max-w-xl leading-relaxed text-slate-400">
                  Sign in to see your assessments, results and certificates. If you were
                  invited by email, the link in that invitation takes you straight into
                  the test — you do not need an account to start.
                </p>
                <p className="mt-3 text-sm text-slate-500">
                  Lost your invitation link? Email{" "}
                  <a
                    href="mailto:assessment@weremotedevs.com"
                    className="text-violet-400 underline-offset-4 hover:underline"
                  >
                    assessment@weremotedevs.com
                  </a>{" "}
                  and we will resend it.
                </p>
              </div>

              <div className="flex flex-col gap-3 md:w-56">
                <Button
                  size="lg"
                  className="w-full bg-gradient-to-r from-violet-500 to-indigo-600 text-white hover:from-violet-600 hover:to-indigo-700"
                  onClick={() => window.open(`${TALENT_OS_URL}/candidate`, "_blank")}
                >
                  Candidate login <FiArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="w-full border-white/20 bg-white/5 text-white hover:bg-white/10 hover:text-white"
                  onClick={() => window.open(`${TALENT_OS_URL}/candidate/vetting`, "_blank")}
                >
                  My results
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* ── FAQ ─────────────────────────────────────────────────────────── */}
      <section className="border-b border-white/5 py-20">
        <div className="container mx-auto max-w-3xl px-4">
          <div className="mb-12 text-center">
            <Badge variant="outline" className="mb-4 border-white/15 text-slate-400">
              FAQ
            </Badge>
            <h2 className="text-3xl font-bold text-white sm:text-4xl">Common questions</h2>
          </div>

          <Accordion type="single" collapsible className="w-full">
            {faqs.map((f, i) => (
              <AccordionItem key={f.q} value={`item-${i}`} className="border-white/10">
                <AccordionTrigger className="text-left text-base font-medium text-white hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-slate-400">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ── CTA ─────────────────────────────────────────────────────────── */}
      <section className="py-24">
        <div className="container mx-auto max-w-2xl px-4 text-center">
          <h2 className="text-3xl font-bold text-white sm:text-4xl">
            Stop interviewing people who cannot do the job
          </h2>
          <p className="mt-4 text-slate-400">
            Generate an assessment for the role you are hiring right now and see the
            questions before anyone is contacted.
          </p>
          <div className="mt-9 flex flex-col justify-center gap-3 sm:flex-row">
            <Button
              size="lg"
              className="bg-gradient-to-r from-violet-500 to-indigo-600 px-8 text-white hover:from-violet-600 hover:to-indigo-700"
              onClick={() => window.open(TALENT_OS_URL, "_blank")}
            >
              Get started <FiArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-white/20 bg-white/5 px-8 text-white hover:bg-white/10 hover:text-white"
              onClick={() =>
                (window.location.href =
                  "mailto:assessment@weremotedevs.com?subject=SkillProof demo request")
              }
            >
              <FiMail className="mr-2 h-4 w-4" /> Talk to us
            </Button>
          </div>
        </div>
      </section>

      {/* ── Footer ──────────────────────────────────────────────────────── */}
      <footer className="border-t border-white/10 py-12">
        <div className="container mx-auto px-4">
          <div className="flex flex-col items-center justify-between gap-8 md:flex-row md:items-start">
            <div>
              <div className="flex items-center gap-2.5">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-violet-500 to-indigo-600">
                  <FiShield className="h-4 w-4 text-white" />
                </div>
                <span className="font-semibold text-white">SkillProof</span>
              </div>
              <p className="mt-3 max-w-xs text-sm text-slate-500">
                Technical assessments generated per role, proctored, and scored on
                evidence. By WeRemoteDevs.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-x-14 gap-y-3 text-sm sm:grid-cols-3">
              <div className="space-y-2.5">
                <div className="text-xs font-medium tracking-wider text-slate-600">PRODUCT</div>
                <a href="#how" className="block text-slate-400 transition-colors hover:text-white">How it works</a>
                <a href="#features" className="block text-slate-400 transition-colors hover:text-white">Features</a>
                <a href={DEMO_REPORT_URL} className="block text-slate-400 transition-colors hover:text-white">Sample report</a>
              </div>
              <div className="space-y-2.5">
                <div className="text-xs font-medium tracking-wider text-slate-600">CANDIDATES</div>
                <a href={`${TALENT_OS_URL}/candidate`} target="_blank" rel="noopener noreferrer" className="block text-slate-400 transition-colors hover:text-white">Login</a>
                <a href={`${TALENT_OS_URL}/candidate/vetting`} target="_blank" rel="noopener noreferrer" className="block text-slate-400 transition-colors hover:text-white">My results</a>
                <a href="mailto:assessment@weremotedevs.com" className="block text-slate-400 transition-colors hover:text-white">Support</a>
              </div>
              <div className="space-y-2.5">
                <div className="text-xs font-medium tracking-wider text-slate-600">COMPANY</div>
                <a href="https://weremotedevs.com" target="_blank" rel="noopener noreferrer" className="block text-slate-400 transition-colors hover:text-white">WeRemoteDevs</a>
                <a href="/privacy-policy" className="block text-slate-400 transition-colors hover:text-white">Privacy</a>
                <a href="/terms-conditions" className="block text-slate-400 transition-colors hover:text-white">Terms</a>
              </div>
            </div>
          </div>

          <div className="mt-10 border-t border-white/10 pt-6 text-center text-xs text-slate-600">
            © {new Date().getFullYear()} WeRemoteDevs. All rights reserved.
          </div>
        </div>
      </footer>

      {/* ── Scoring methodology ─────────────────────────────────────────── */}
      {/* Everything in this dialog is stated by the engine, not marketing copy.
          Weights come from _shared/questionWeights.ts, the percentage from
          generate-report (totalScore / totalMaxScore), and the integrity
          deductions from PROCTORING_PENALTIES. If any of those change, change
          this too — a public page that contradicts the engine is how a score
          dispute starts. */}
      <Dialog open={showScoringDialog} onOpenChange={setShowScoringDialog}>
        <DialogContent className="w-[calc(100vw-1.5rem)] max-w-3xl overflow-hidden border-white/10 bg-slate-950 p-0 text-white sm:w-full">
          <DialogHeader className="border-b border-white/10 px-6 pb-4 pt-6">
            <DialogTitle className="text-left text-xl font-bold text-white">
              Scoring methodology
            </DialogTitle>
            <DialogDescription className="text-left text-sm text-slate-400">
              How a SkillProof percentage is arrived at, exactly.
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="max-h-[70vh] px-6 py-5">
            <div className="space-y-6">
              {/* The formula */}
              <div className="rounded-xl border border-violet-400/25 bg-violet-500/10 p-5">
                <h3 className="mb-3 flex items-center gap-2 font-semibold text-white">
                  <FiTarget className="h-4 w-4 text-violet-400" /> The overall score
                </h3>
                <div className="mb-3 rounded-lg bg-black/40 p-4 font-mono text-sm text-violet-300">
                  score % = points earned ÷ points available × 100
                </div>
                <p className="text-sm leading-relaxed text-slate-300">
                  That is the whole formula. Proctoring flags are{" "}
                  <strong className="text-white">not</strong> subtracted from it — the
                  integrity score is reported as a separate number so you can see ability
                  and conduct independently and decide what to do about each.
                </p>
              </div>

              {/* Weights */}
              <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5">
                <h3 className="mb-2 flex items-center gap-2 font-semibold text-white">
                  <FiActivity className="h-4 w-4 text-violet-400" /> What each question is worth
                </h3>
                <p className="mb-4 text-sm text-slate-400">
                  Weight is set by question type and difficulty, so a hard coding problem
                  counts for several multiple-choice questions rather than the same as one.
                  A question with a weight written by hand keeps that weight.
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-white/10 text-xs tracking-wider text-slate-500">
                        <th className="py-2 text-left font-medium">TYPE</th>
                        <th className="py-2 text-right font-medium">EASY</th>
                        <th className="py-2 text-right font-medium">MEDIUM</th>
                        <th className="py-2 text-right font-medium">HARD</th>
                      </tr>
                    </thead>
                    <tbody className="text-slate-300">
                      {[
                        ["Multiple choice", 1, 2, 3],
                        ["Coding MCQ", 1, 2, 3],
                        ["Fill in the blank", 1, 2, 3],
                        ["Scenario", 3, 4, 5],
                        ["Live coding", 5, 8, 10],
                        ["System design", 10, 12, 15],
                        ["Essay", 10, 12, 15],
                      ].map(([name, e, m, h]) => (
                        <tr key={String(name)} className="border-b border-white/5">
                          <td className="py-2.5">{name}</td>
                          <td className="py-2.5 text-right tabular-nums">{e}</td>
                          <td className="py-2.5 text-right tabular-nums">{m}</td>
                          <td className="py-2.5 text-right tabular-nums">{h}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Marking per type */}
              <div className="grid gap-4 sm:grid-cols-2">
                {[
                  {
                    icon: <FiCheckCircle className="h-4 w-4 text-sky-400" />,
                    title: "Multiple choice & fill-in",
                    lines: [
                      "Correct scores the full weight",
                      "Wrong scores zero — no negative marking",
                      "Fill-in matching is case-insensitive",
                      "Unanswered scores zero and still counts",
                    ],
                  },
                  {
                    icon: <FiCode className="h-4 w-4 text-violet-400" />,
                    title: "Live coding",
                    lines: [
                      "Compiled and run against test cases",
                      "Partial credit in proportion to tests passed",
                      "Half the tests passing earns half the weight",
                      "Graded on behaviour, not on how it reads",
                    ],
                  },
                  {
                    icon: <FiCpu className="h-4 w-4 text-amber-400" />,
                    title: "Essay & system design",
                    lines: [
                      "Graded by AI against a written rubric",
                      "Judged on accuracy, depth and structure",
                      "Awarded as a fraction of the weight",
                      "Open to manual review before you decide",
                    ],
                  },
                  {
                    icon: <FiBarChart2 className="h-4 w-4 text-emerald-400" />,
                    title: "Breakdowns",
                    lines: [
                      "Every question is tagged with a skill",
                      "Scores roll up per skill and per type",
                      "Sub-totals reconcile to the overall score",
                      "Pass or fail is against the mark you set",
                    ],
                  },
                ].map((b) => (
                  <div key={b.title} className="rounded-xl border border-white/10 bg-white/[0.03] p-5">
                    <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold text-white">
                      {b.icon} {b.title}
                    </h4>
                    <ul className="space-y-1.5 text-sm text-slate-400">
                      {b.lines.map((l) => (
                        <li key={l}>• {l}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>

              {/* Integrity */}
              <div className="rounded-xl border border-amber-400/25 bg-amber-500/[0.07] p-5">
                <h3 className="mb-2 flex items-center gap-2 font-semibold text-white">
                  <FiShield className="h-4 w-4 text-amber-400" /> The integrity score
                </h3>
                <p className="mb-4 text-sm text-slate-400">
                  Starts at 100 and is reduced as proctoring events are recorded. It is
                  reported alongside the skill score and never deducted from it.
                </p>
                <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
                  {[
                    ["AI content detected", 25],
                    ["AI voice detected", 20],
                    ["Phone detected", 15],
                    ["Multiple faces", 15],
                    ["Paste detected", 15],
                    ["Tab switch", 10],
                    ["Left fullscreen", 10],
                    ["Copy attempt", 5],
                    ["Face not visible", 5],
                  ].map(([label, n]) => (
                    <div key={String(label)} className="rounded-lg border border-white/5 bg-black/30 p-3 text-center">
                      <div className="font-semibold text-amber-300">−{n}</div>
                      <div className="mt-0.5 text-[11px] leading-tight text-slate-500">{label}</div>
                    </div>
                  ))}
                </div>
                <p className="mt-4 text-xs text-slate-500">
                  Every event is stored with its timestamp and listed on the report, so a
                  low integrity score can always be traced to what actually happened.
                </p>
              </div>
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Index;
